import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useApi } from '../lib/api'
import { Spinner } from '../components/Spinner'
import type { Newsletter } from '../lib/types'
import { Plus } from 'lucide-react'

export function Newsletters() {
  const { call } = useApi()
  const [rows, setRows] = useState<Newsletter[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    call<Newsletter[]>('/newsletters')
      .then((data) => {
        if (!cancelled) setRows(data)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })
    return () => {
      cancelled = true
    }
  }, [call])

  return (
    <div>
      <header className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-marrow-900 dark:text-bone-50">
            Newsletters
          </h1>
          <p className="mt-1 text-sm text-marrow-800/70 dark:text-bone-200/70">
            Issues live in the iOS app once their <code className="rounded bg-bone-100 px-1.5 py-0.5 font-mono text-xs dark:bg-marrow-800">published_at</code> is in the past.
          </p>
        </div>
        <Link
          to="/newsletters/new"
          className="flex items-center gap-2 rounded-md bg-rust-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-rust-600"
        >
          <Plus size={16} aria-hidden /> New
        </Link>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {rows === null ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-bone-300 bg-bone-100/40 p-12 text-center text-sm text-marrow-800/70 dark:border-marrow-700 dark:bg-marrow-800/40 dark:text-bone-200/70">
          No newsletters yet. Click <strong>New</strong> to author the first one.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-bone-200 bg-white dark:border-marrow-700 dark:bg-marrow-800">
          <table className="w-full text-sm">
            <thead className="border-b border-bone-200 bg-bone-100/40 text-xs uppercase tracking-wider text-marrow-800/60 dark:border-marrow-700 dark:bg-marrow-700/40 dark:text-bone-200/60">
              <tr>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Slug</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((n) => (
                <tr
                  key={n.id}
                  className="border-b border-bone-200 last:border-b-0 hover:bg-bone-50 dark:border-marrow-700 dark:hover:bg-marrow-700/40"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/newsletters/${n.id}`}
                      className="font-medium text-marrow-900 hover:text-rust-600 dark:text-bone-50 dark:hover:text-rust-500"
                    >
                      {n.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-marrow-800/70 dark:text-bone-200/70">
                    {n.slug}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill published_at={n.published_at} />
                  </td>
                  <td className="px-4 py-3 text-marrow-800/70 dark:text-bone-200/70">
                    {new Date(n.updated_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function StatusPill({ published_at }: { published_at: string | null }) {
  if (!published_at) {
    return (
      <span className="rounded-full bg-bone-200 px-2 py-0.5 text-xs font-medium text-marrow-800 dark:bg-marrow-700 dark:text-bone-200">
        Draft
      </span>
    )
  }
  const ts = new Date(published_at).getTime()
  const now = Date.now()
  if (ts > now) {
    return (
      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
        Scheduled
      </span>
    )
  }
  return (
    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
      Published
    </span>
  )
}
